import React, { useEffect } from 'react';
import Button from '@/components/@base/button';
import Container from '@/components/@base/container';
import { Text } from '@/components/@base/typography';
import Banner from '@/components/desktop/banner';
import { SIZE_ENUM } from '@/components/@base/@types';
import useHomeStore from '@/store/home';
import useBottomSheetStore from '@/store/bottom-sheet-store';
import useCommonModalStore from '@/store/common-modal-store';
import useUserWalletsVouchersStore from '@/store/user-wallets-store';
import { useGetProviderVisibility, usePostCreateTransactions } from '@/services/hooks';
import { selectedSegments, selectedVouchers } from '@/services/models/api.models';
import PreInvoice from '@/components/pre-invoice';
import UserCanSelectAssets from '@/components/user-can-select-assets';
import localization from '@/constant/localization';
import { currency, IsUpgData } from '@/lib/helper';
import AddWalletWithId from '@/components/shared/add-wallet-with-id';
import cn from '@/lib/clsxm';

const OrderSummary = () => {
  const { showBottomSheet } = useBottomSheetStore();
  const { setShow } = useCommonModalStore();
  const { segments, usableClientVouchers, isSegmentLoading } = useUserWalletsVouchersStore();
  const { invoice, remainInvoice, setRemainInvoice, isActiveRequest, setIsActiveRequest } =
    useHomeStore();

  const { mutate, isPending } = usePostCreateTransactions();
  const { data:showKeepa, isLoading:isLoadingShowKeepa } = useGetProviderVisibility();
  const checkUserSelectedAssetsOrDontHaveAssets = () => {
    if (remainInvoice === 0) {
      return true;
    } else if (
      sessionStorage.getItem('walletsIsEmpty') &&
      sessionStorage.getItem('vouchersIsEmpty')
    ) {
      return true;
    } else return !!sessionStorage.getItem('isSelectedVoucherOrWallet');
  };

  const showPreInvoice = () => {
    if (!isActiveRequest && checkUserSelectedAssetsOrDontHaveAssets()) {
      setIsActiveRequest(true);
      const theSelectedSegments: selectedSegments[] = [];
      const theSelectedVouchers: selectedVouchers[] = [];
      segments?.map((s) => {
        if (s.selected && s.usageBalance) {
          theSelectedSegments.push({
            segmentId: s.segmentId,
            amount: s.usageBalance,
          });
        }
      });
      usableClientVouchers?.map((s) => {
        if (s.selected) {
          theSelectedVouchers.push({
            voucherId: s.voucherId,
          });
        }
      });

      mutate(
        {
          selectedSegments: theSelectedSegments,
          selectedVouchers: theSelectedVouchers,
        },
        {
          onSuccess: (res) => {
            setIsActiveRequest(false);
            setShow(true, {
              onDemandClose: true,
              Body: () => <PreInvoice data={res} />,
            });
          },
          onError: () => {
            setIsActiveRequest(false);
          },
        },
      );
    } else if (!sessionStorage.getItem('isSelectedVoucherOrWallet')) {
      setShow(true, {
        center: true,
        onDemandClose: true,
        dialogPanelClassName: 'px-0 pb-0 w-[280px]',
        Body: () => (
          <UserCanSelectAssets
            handleGoToPayment={() => {
              mutate(
                {
                  selectedSegments: [],
                  selectedVouchers: [],
                },
                {
                  onSuccess: (res) => {
                    setIsActiveRequest(false);
                    setShow(true, {
                      onDemandClose: true,
                      Body: () => <PreInvoice data={res} />,
                    });
                  },
                  onError: () => {
                    setIsActiveRequest(false);
                  },
                },
              );
            }}
          />
        ),
      });
    }
  };
  useEffect(() => {
    let selectedSegmentsAndVouchersAmount = 0;
    segments?.map((s) => {
      if (s.selected && s.usageBalance) {
        selectedSegmentsAndVouchersAmount += s.usageBalance;
      } else if (s.selected) {
        selectedSegmentsAndVouchersAmount += s.balance;
      }
    });
    usableClientVouchers?.map((s) => {
      if (s.selected && s.usageBalance) {
        selectedSegmentsAndVouchersAmount += s.usageBalance;
      }
    });
    setRemainInvoice(invoice - selectedSegmentsAndVouchersAmount);
  }, [segments, usableClientVouchers, showBottomSheet, invoice]);
  if (segments.length === 0 && usableClientVouchers.length === 0 && !isSegmentLoading && showKeepa?.showKeepaPlaceHolder && !isLoadingShowKeepa) {
    return (
      <AddWalletWithId
        showPreInvoice={showPreInvoice}
        isLoading={isPending}
        className='mt-4'
      />
    );
  }
  return (
    <Container>
      <Container
        center
        className={cn('mt-3 w-full justify-between gap-2 p-4 pl-[25px] pt-5 md:justify-start', IsUpgData() && "mt-2")}
      >
        <Text className='text-lg text-grayLight-1000 dark:text-grayLight-0' medium>
          {localization.orderSummery}
        </Text>
      </Container>

      <Container className='rounded-xl border border-gray-100 bg-grayLight-50 p-6  dark:border-grayDark-700 dark:bg-grayDark-750'>
        <Banner />
        <Container className='mb-8 mt-4 space-y-4'>
          <Container className='text-md flex justify-between'>
            <Text className='flex gap-1 text-black dark:text-white'>
              <span className='text-gray-500 dark:text-white'>
                {localization.invoiceBalance}
              </span>
            </Text>
            <Container className='dark:text-white'>
              <span>{currency(invoice)}</span>{' '}
              <span className='text-xs font-medium'>{localization.rial}</span>
            </Container>
          </Container>
          <Container className='text-md flex justify-between'>
            <span className='text-gray-500 dark:text-white'>{localization.remainPrice}</span>
            <Container className='dark:text-white'>
              {currency(remainInvoice)}{' '}
              <span className='text-xs font-medium'>{localization.rial}</span>
            </Container>
          </Container>
          <Container className='flex items-center justify-between border-t border-gray-300 pt-4'>
            <span className='font-semibold text-gray-600 dark:text-white'>
              {localization.theAmountPayable}
            </span>
            <Container className='dark:text-white'>
              {currency(remainInvoice)}{' '}
              <span className='text-xs font-medium'>{localization.rial}</span>
            </Container>
          </Container>
        </Container>

        <Button
          size={SIZE_ENUM.XL}
          onClick={showPreInvoice}
          className='w-full rounded-full bg-black font-bold text-white'
        >
          {remainInvoice === 0 ? localization.payment : localization.remainPayment}
        </Button>
      </Container>
    </Container>
  );
};

export default OrderSummary;
